import { Eye, Edit } from 'lucide-react'
import { TabsContent } from '../ui/tabs'
import { Input } from '../ui/input'
import { Badge } from '../ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select'
import { PriceAdjustment, Supplier } from '../../services/purchases.service'

interface PriceAdjustmentsTabProps {
  priceAdjustments: PriceAdjustment[]
  suppliers: Supplier[]
  adjustmentFilters: {
    supplier: string
    status: string
    dateFrom: string
    dateTo: string
  }
  setAdjustmentFilters: (filters: {
    supplier: string
    status: string
    dateFrom: string
    dateTo: string
  }) => void
  openPriceAdjustmentModal: (adjustment: PriceAdjustment) => void
}

export function PriceAdjustmentsTab({
  priceAdjustments,
  suppliers,
  adjustmentFilters,
  setAdjustmentFilters,
  openPriceAdjustmentModal,
}: PriceAdjustmentsTabProps) {
  const filteredAdjustments = priceAdjustments.filter((adj) => {
    const matchSupplier =
      !adjustmentFilters.supplier ||
      adjustmentFilters.supplier === 'all' ||
      adj.supplier === adjustmentFilters.supplier
    const matchStatus =
      !adjustmentFilters.status ||
      adjustmentFilters.status === 'all' ||
      adj.status === adjustmentFilters.status
    const matchDateFrom =
      !adjustmentFilters.dateFrom || adj.date >= adjustmentFilters.dateFrom
    const matchDateTo =
      !adjustmentFilters.dateTo || adj.date <= adjustmentFilters.dateTo
    return matchSupplier && matchStatus && matchDateFrom && matchDateTo
  })

  return (
    <TabsContent value="adjustments" className="space-y-4">
      {/* Filters */}
      <div className="rounded-lg border border-slate-200 bg-white p-4">
        <h3 className="mb-4 font-medium text-slate-900">Filtros</h3>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-700">
              Proveedor
            </label>
            <Select
              value={adjustmentFilters.supplier}
              onValueChange={(value) =>
                setAdjustmentFilters({
                  ...adjustmentFilters,
                  supplier: value,
                })
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Todos" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {suppliers.map((supplier) => (
                  <SelectItem key={supplier.id} value={supplier.name}>
                    {supplier.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-700">Estado</label>
            <Select
              value={adjustmentFilters.status}
              onValueChange={(value) =>
                setAdjustmentFilters({
                  ...adjustmentFilters,
                  status: value,
                })
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Todos" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="pending">Pendiente</SelectItem>
                <SelectItem value="updated">Actualizado</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-700">
              Fecha Desde
            </label>
            <Input
              type="date"
              value={adjustmentFilters.dateFrom}
              onChange={(e) =>
                setAdjustmentFilters({
                  ...adjustmentFilters,
                  dateFrom: e.target.value,
                })
              }
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-700">
              Fecha Hasta
            </label>
            <Input
              type="date"
              value={adjustmentFilters.dateTo}
              onChange={(e) =>
                setAdjustmentFilters({
                  ...adjustmentFilters,
                  dateTo: e.target.value,
                })
              }
            />
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-600">
          {filteredAdjustments.length} ajustes encontrados
        </p>
      </div>

      <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
        <table className="w-full">
          <thead className="border-b border-slate-200 bg-slate-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                ID
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                Proveedor
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                Fecha
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                ID Compra
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                Productos
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                Estado
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                Fecha Modificación
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                Acciones
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {filteredAdjustments.map((adjustment) => (
              <tr key={adjustment.id} className="hover:bg-slate-50">
                <td className="px-6 py-4 font-medium text-slate-900">
                  {adjustment.id}
                </td>
                <td className="px-6 py-4 text-slate-700">
                  {adjustment.supplier}
                </td>
                <td className="px-6 py-4 text-slate-700">{adjustment.date}</td>
                <td className="px-6 py-4 text-slate-700">
                  {adjustment.purchaseId}
                </td>
                <td className="px-6 py-4 text-slate-700">
                  {adjustment.items} productos
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center gap-2">
                    <Badge
                      className={
                        adjustment.status === 'pending'
                          ? 'bg-yellow-100 text-yellow-700'
                          : 'bg-green-100 text-green-700'
                      }
                    >
                      {adjustment.status === 'pending'
                        ? 'Pendiente'
                        : 'Actualizado'}
                    </Badge>
                    {adjustment.isEdited && (
                      <Badge className="bg-purple-100 text-purple-700">
                        Editada
                      </Badge>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 text-slate-700">
                  {adjustment.editDate || '-'}
                </td>
                <td className="px-6 py-4">
                  <div className="flex gap-2">
                    <button
                      onClick={() => openPriceAdjustmentModal(adjustment)}
                      className="rounded p-1 hover:bg-slate-100"
                      title={
                        adjustment.status === 'pending'
                          ? 'Ver detalles'
                          : 'Ver/Editar'
                      }
                    >
                      <Eye className="h-4 w-4 text-slate-600" />
                    </button>
                    {adjustment.status === 'updated' && (
                      <button
                        onClick={() => openPriceAdjustmentModal(adjustment)}
                        className="rounded p-1 hover:bg-slate-100"
                        title="Editar"
                      >
                        <Edit className="h-4 w-4 text-primary" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </TabsContent>
  )
}
